/* ============================================================================
   LOS PROCESOS DE LA APLICACIÓN

   ══════════════════════════════════════════════════════════════════════════
   POR QUÉ EXISTE ESTE MÓDULO

   La aplicación está organizada por MÓDULOS —lo que el software sabe hacer— y
   el piloto trabaja por PROCESOS —lo que hace y en qué momento.

   El orden ya existe: no se puede calcular el peso y balance sin saber qué se
   carga, ni el FRAT sin saber la altitud de crucero. Los propios paneles lo
   dicen: «Variables del vuelo (desde Análisis)», «Nivel de vuelo (de
   Planificación de ruta)».

   **Lo que no existe es que la pantalla lo diga.** Hoy hay que saberlo de
   memoria, y si se saltea un paso nada lo advierte hasta que del otro lado
   falta el dato.

   ══════════════════════════════════════════════════════════════════════════
   LA REGLA QUE ORDENA TODO ESTE ARCHIVO

   **No saber no es saber que no, y tampoco es saber que sí.**

   Un requisito tiene tres estados, no dos: cumplido, incumplido, y SIN DATO.
   Un paso con un requisito sin dato **no está listo**, y tampoco está mal: no
   se sabe. Decir «listo» cuando un dato falta es exactamente el error que
   hace despegar a alguien creyendo que verificó algo.

   Por eso `sePuedeVolar` es verdadero **solo si todos los requisitos que
   bloquean están cumplidos**. Un `null` nunca alcanza.
   ══════════════════════════════════════════════════════════════════════════
   ========================================================================== */

/* ═══════════════════════════════════════════════════════════════════════════
   LOS SEIS PROCESOS

   Separados por MOMENTO y FRECUENCIA, no por módulo. Lo que se carga una vez
   al año no tiene por qué ocupar la pantalla que se usa todos los días.
   ═══════════════════════════════════════════════════════════════════════════ */
export const PROCESOS = [
  {
    id: "configurar",
    nombre: "Alta del operador",
    cuando: "Una vez, en oficina, con conexión",
    quien: "El operador",
    descripcion: "Flota, perfil por tipo, pesaje, tripulación con licencias, "
      + "programa de mantenimiento, umbrales declarados y claves de mapa.",
  },
  {
    id: "preparar",
    nombre: "Preparar el vuelo",
    cuando: "En tierra, antes de salir",
    quien: "El piloto",
    descripcion: "Ruta, carga, peso y balance, combustible, performance, FRAT "
      + "y hoja de vuelo. Tiene un orden obligatorio.",
  },
  {
    id: "volar",
    nombre: "Volar",
    cuando: "En el aire",
    quien: "El piloto",
    descripcion: "Modo vuelo, cronómetro, traza y avisos.",
  },
  {
    id: "cerrar",
    nombre: "Cerrar el vuelo",
    cuando: "Al apagar",
    quien: "El piloto",
    descripcion: "Cierre del vuelo con FT y AT, asiento al libro, "
      + "discrepancias, cierre del día y respaldo.",
  },
  {
    id: "gestionar",
    nombre: "Gestionar",
    cuando: "Periódico, en oficina",
    quien: "El operador y el SIG",
    descripcion: "SGS, estado de mantenimiento, duty time, currency y "
      + "autoevaluación BARS.",
  },
  {
    id: "consultar",
    nombre: "Consultar",
    cuando: "Cuando hace falta",
    quien: "Cualquiera",
    descripcion: "Matrices, criterios, escenarios, glosario y biblioteca de "
      + "eventos. Acá no se llena nada.",
  },
];

/* ═══════════════════════════════════════════════════════════════════════════
   LOS PASOS DE LA PREPARACIÓN DEL VUELO

   Cada requisito declara si BLOQUEA. La distinción importa y no es cosmética:

   · Bloquea = sin eso no se despega. Peso fuera de envolvente, ruta sin
     destino, combustible por debajo del requerido.
   · No bloquea = la pantalla queda incompleta pero el vuelo es posible. El
     perfil del terreno es información; su ausencia no impide volar, aunque
     volar sin saber la elevación del valle sea otra discusión.

   **Un requisito que no bloquea NO se esconde**: se muestra como pendiente.
   Esconder lo que no bloquea haría que el paso dijera «listo» con la mitad
   sin llenar.
   ═══════════════════════════════════════════════════════════════════════════ */
export const PASOS_PREPARACION = [
  {
    id: "ruta",
    nombre: "Ruta",
    pestana: "ruta",
    descripcion: "De dónde a dónde, por dónde y sobre qué terreno.",
    requisitos: [
      { clave: "origen", etiqueta: "Punto de salida", bloquea: true },
      { clave: "destino", etiqueta: "Punto de llegada", bloquea: true },
      { clave: "distancia", etiqueta: "Distancia calculada", bloquea: true },
      { clave: "perfilTerreno", etiqueta: "Perfil del terreno bajo la ruta", bloquea: false },
      { clave: "nivelCrucero", etiqueta: "Nivel de vuelo de crucero", bloquea: true },
    ],
  },
  {
    id: "analisis",
    nombre: "Peso y balance",
    pestana: "analisis",
    descripcion: "Qué aeronave, qué lleva, cuánto pesa y dónde queda el CG.",
    requisitos: [
      { clave: "aeronave", etiqueta: "Aeronave seleccionada", bloquea: true },
      { clave: "pesoVacio", etiqueta: "Peso vacío de la matrícula", bloquea: true },
      { clave: "carga", etiqueta: "Carga y pasajeros declarados", bloquea: true },
      { clave: "dentroEnvolvente", etiqueta: "Peso y CG dentro de envolvente", bloquea: true },
      { clave: "cgAterrizaje", etiqueta: "CG en aterrizaje dentro de envolvente", bloquea: true },
      { clave: "performance", etiqueta: "Performance en el punto de salida", bloquea: false },
    ],
  },
  {
    id: "combustible",
    nombre: "Combustible",
    pestana: "analisis",
    descripcion: "Cuánto se embarca contra cuánto pide la ruta más la reserva.",
    requisitos: [
      { clave: "embarcado", etiqueta: "Combustible embarcado", bloquea: true },
      { clave: "requerido", etiqueta: "Requerido por la ruta (con reserva)", bloquea: true },
      { clave: "alcanza", etiqueta: "El embarcado cubre el requerido", bloquea: true },
    ],
  },
  {
    id: "frat",
    nombre: "FRAT",
    pestana: "frat",
    descripcion: "El riesgo del vuelo, por el formulario aprobado del operador.",
    /* ═══ NO HAY REQUISITO «FORMULARIO CONTESTADO», Y NO ES UN OLVIDO ═══

       `riskChoices` arranca con TODOS los grupos en el índice 0, así que el
       FRAT está contestado desde que se abre la aplicación. No existe forma
       de distinguir «lo contestó» de «no lo tocó».

       Poner ese requisito daría siempre verde y sería **una afirmación falsa
       con forma de verificación**. Se declaran los tres que sí se pueden
       medir. Que el formulario arranque precontestado es un problema aparte,
       anotado para el operador. */
    requisitos: [
      { clave: "tipoDeTarea", etiqueta: "Tipo de tarea declarado", bloquea: true },
      { clave: "nivelAceptable", etiqueta: "Nivel de riesgo aceptable", bloquea: true },
      { clave: "medidasDeclaradas", etiqueta: "Medidas donde el riesgo las exige", bloquea: true },
    ],
  },
  {
    id: "hoja",
    nombre: "Hoja de vuelo",
    pestana: "resumen",
    descripcion: "El resumen que se firma y se lleva.",
    requisitos: [
      { clave: "piloto", etiqueta: "Piloto asignado", bloquea: true },
      { clave: "confirmado", etiqueta: "Confirmación del piloto", bloquea: true },
    ],
  },
];

/* Los tres estados de un requisito. Un cuarto no hace falta y un segundo no
   alcanza. */
export const CUMPLIDO = "cumplido";
export const FALTA = "falta";
export const SIN_DATO = "sinDato";

/**
 * El estado de UN requisito a partir del hecho que le corresponde.
 *
 * `true` → cumplido. `false` → falta. **`null` o `undefined` → SIN DATO**, que
 * no es lo mismo: no saber si el peso está dentro de la envolvente es distinto
 * de saber que está afuera, y las dos cosas son distintas de estar adentro.
 */
export function estadoDeRequisito(hecho) {
  if (hecho === true) return CUMPLIDO;
  if (hecho === false) return FALTA;
  return SIN_DATO;
}

/**
 * El estado de un paso.
 *
 * Devuelve qué falta y qué no se sabe, por separado y con las etiquetas
 * escritas: un paso que dice «incompleto» sin decir qué le falta obliga a
 * recorrer la pantalla entera buscando.
 */
export function estadoDePaso(paso, hechos = {}) {
  const requisitos = (paso?.requisitos || []).map((r) => ({
    ...r,
    estado: estadoDeRequisito(hechos[r.clave]),
  }));

  const faltan = requisitos.filter((r) => r.estado === FALTA);
  const sinDato = requisitos.filter((r) => r.estado === SIN_DATO);

  /* Bloquean los que bloquean, estén en falta O sin dato: no saber no
     habilita. */
  const bloqueantes = [...faltan, ...sinDato].filter((r) => r.bloquea);

  const listo = faltan.length === 0 && sinDato.length === 0;

  return {
    id: paso.id,
    nombre: paso.nombre,
    pestana: paso.pestana,
    descripcion: paso.descripcion,
    requisitos,
    faltan,
    sinDato,
    bloqueantes,
    listo,
    /* `bloquea` es la pregunta que importa en la barra: ¿este paso impide
       despegar? */
    bloquea: bloqueantes.length > 0,
    /* Para pintar: listo / parcial / vacío. «parcial» es haber empezado. */
    avance: listo
      ? "listo"
      : requisitos.some((r) => r.estado === CUMPLIDO) ? "parcial" : "vacio",
  };
}

/**
 * El camino completo.
 *
 * `sePuedeVolar` es la única afirmación fuerte de este módulo, y por eso exige
 * que **todos** los requisitos bloqueantes estén cumplidos. Un solo `null`
 * alcanza para que sea `false`: la aplicación no puede decir que se puede
 * despegar sobre un dato que no tiene.
 */
export function estadoDelCamino(hechos = {}, pasos = PASOS_PREPARACION) {
  const estados = pasos.map((p) => estadoDePaso(p, hechos));
  const bloqueantes = estados.flatMap((e) => e.bloqueantes);

  return {
    pasos: estados,
    bloqueantes,
    sePuedeVolar: bloqueantes.length === 0,
    /* El primero sin terminar: es a dónde lleva el botón «seguir». */
    siguiente: estados.find((e) => !e.listo) || null,
    listos: estados.filter((e) => e.listo).length,
    total: estados.length,
  };
}

/**
 * Una línea corta que diga qué falta.
 *
 * Nombra **lo primero que bloquea**, no un conteo. «Faltan 3 cosas» manda a
 * buscar; «falta el peso vacío de la matrícula» se resuelve.
 */
export function queFalta(camino) {
  if (!camino || camino.sePuedeVolar) return null;

  const primero = camino.bloqueantes[0];
  const otros = camino.bloqueantes.length - 1;

  const cola = otros > 0
    ? ` y ${otros} ${otros === 1 ? "cosa más" : "cosas más"}`
    : "";

  const motivo = primero.estado === SIN_DATO ? "sin dato" : "falta";

  return `${primero.etiqueta} — ${motivo}${cola}`;
}

/* ═══════════════════════════════════════════════════════════════════════════
   EL MAPA DE CAMPOS

   Dónde vive hoy cada grupo de datos y a qué proceso PERTENECE. La diferencia
   entre las dos columnas es el trabajo que queda por hacer.

   Este mapa existe para que mover un campo de pantalla no lo pierda: la
   reorganización se verifica contra esta lista, en vez de contra la memoria
   de quien la hizo.
   ═══════════════════════════════════════════════════════════════════════════ */
export const MAPA_DE_CAMPOS = [
  { grupo: "Flota y matrículas", hoy: "mantenimiento", proceso: "configurar" },
  { grupo: "Perfil por tipo de aeronave", hoy: "importar", proceso: "configurar" },
  { grupo: "Pesaje — peso vacío por matrícula", hoy: "mantenimiento", proceso: "configurar" },
  { grupo: "Pilotos, licencia y apto médico", hoy: "operaciones", proceso: "configurar" },
  { grupo: "Mecánicos y supervisores", hoy: "operaciones", proceso: "configurar" },
  { grupo: "Programa de inspecciones y AD", hoy: "mantenimiento", proceso: "configurar" },
  { grupo: "Componentes de vida límite", hoy: "mantenimiento", proceso: "configurar" },
  { grupo: "Umbrales de jornada por jurisdicción", hoy: "operaciones", proceso: "configurar" },
  { grupo: "Clave de MapTiler y mapas offline", hoy: "ruta", proceso: "configurar" },

  { grupo: "Puntos de ruta y mis puntos", hoy: "ruta", proceso: "preparar" },
  { grupo: "Nivel de crucero y perfil vertical", hoy: "ruta", proceso: "preparar" },
  { grupo: "Carga, pasajeros y manifiesto", hoy: "analisis", proceso: "preparar" },
  { grupo: "Combustible embarcado", hoy: "analisis", proceso: "preparar" },
  { grupo: "Respuestas del FRAT", hoy: "frat", proceso: "preparar" },
  { grupo: "Confirmación del piloto", hoy: "ruta", proceso: "preparar" },

  { grupo: "Cronómetro de vuelo", hoy: "operaciones", proceso: "cerrar" },
  { grupo: "Vuelos de la jornada", hoy: "operaciones", proceso: "cerrar" },
  { grupo: "Discrepancias de mantenimiento", hoy: "operaciones", proceso: "cerrar" },
  { grupo: "Libro de vuelo del piloto", hoy: "operaciones", proceso: "cerrar" },

  { grupo: "Reportes voluntarios (ASR)", hoy: "sms", proceso: "gestionar" },
  { grupo: "Eventos indeseables en seguimiento", hoy: "sms", proceso: "gestionar" },
  { grupo: "Política de seguridad", hoy: "sms", proceso: "gestionar" },
  { grupo: "Auditorías y no conformidades", hoy: "dashboard", proceso: "gestionar" },
  { grupo: "Estado de mantenimiento", hoy: "mantenimiento", proceso: "gestionar" },

  { grupo: "Matriz de riesgo y criterios", hoy: "sms", proceso: "consultar" },
  { grupo: "Matriz coste/beneficio", hoy: "sms", proceso: "consultar" },
  { grupo: "Escenarios de accidente (Anexo 3)", hoy: "sms", proceso: "consultar" },
  { grupo: "Glosario", hoy: "sms", proceso: "consultar" },
  { grupo: "Biblioteca de eventos (Anexo 1)", hoy: "sms", proceso: "consultar" },
  { grupo: "Diagrama de corbatín", hoy: "sms", proceso: "consultar" },
];

/** Los grupos que hoy están en una pestaña que no corresponde a su proceso. */
export function camposFueraDeLugar(pestanaDelProceso) {
  return MAPA_DE_CAMPOS.filter((c) => pestanaDelProceso[c.proceso] !== c.hoy);
}
